import { useEffect, useMemo, useState } from "react";
import { useFeature } from "@nextiva/feature-toggle";
import {
  getStoredSelection,
  STORED_SELECTION_EVENT,
  STORED_SELECTION_KEY,
} from "@nextiva/next-ui-wayfinder";
import type { StoredSelection } from "@nextiva/next-ui-wayfinder";
import { useGetAdminGroupHierarchy } from "hooks/useAdminGroup/useGetAdminGroupHierarchy";
import AppData from "app_data";

export type WayfinderScope =
  | { kind: "NONE" }
  | { kind: "ADMIN_GROUP"; id: string }
  | { kind: "LOCATION"; id: string }
  /**
   * A selection exists but can't be resolved into a scope (e.g. a LOCATION
   * with a blank id). Consumers must fail closed: treating this as NONE
   * would show account-wide data while the breadcrumb claims a location.
   */
  | { kind: "INVALID" };

interface UseScopedWayfinderSelectionResult {
  scope: WayfinderScope;
  isLbaEnabled: boolean;
  /**
   * True while the LBA flag hasn't resolved AND a stored selection exists.
   * Consumers must hold rendering: firing unscoped requests in that window
   * would flash out-of-scope data.
   */
  isFlagResolving: boolean;
}

const NO_SCOPE: WayfinderScope = { kind: "NONE" };
const INVALID_SCOPE: WayfinderScope = { kind: "INVALID" };

const sameSelection = (
  a: StoredSelection | null,
  b: StoredSelection | null,
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
};

// Wayfinder's own useWayfinderSelection()/useAdminGroupsHierarchyQuery() read
// the corp account from @nextiva/global-store's Redux slice, which this app
// never populates -- they'd silently resolve to no selection. Mirror
// AdminGroupBreadCrumbWrapper.tsx instead: use AppData.user?.enterprise_id and
// this app's own hierarchy query/cache. AppData is a non-reactive singleton,
// but both route trees gate rendering on `isAuthenticated`, set only after
// loadUser() assigns AppData.user -- so enterprise_id is resolved by mount.
export const useScopedWayfinderSelection =
  (): UseScopedWayfinderSelectionResult => {
    const currentCorp: string | number | undefined =
      AppData.user?.enterprise_id;

    const { isEnabled: isLbaEnabled, isReady: isLbaReady } = useFeature({
      name: "Analytics.LocationBasedAdministration",
    });
    const lbaEnabled = isLbaEnabled && isLbaReady;

    const [selection, setSelection] = useState<StoredSelection | null>(() =>
      getStoredSelection(currentCorp),
    );

    useEffect(() => {
      // getStoredSelection returns a fresh object per call (JSON.parse);
      // without the equality guard every event would change state identity
      // and refetch every mounted chart.
      const refresh = () => {
        setSelection((prev) => {
          const next = getStoredSelection(currentCorp);
          return sameSelection(prev, next) ? prev : next;
        });
      };
      // Cross-tab storage events fire for every key on the origin; only
      // react to the wayfinder selection key (null means storage.clear).
      const handleStorage = (event: StorageEvent) => {
        if (event.key !== null && event.key !== STORED_SELECTION_KEY) return;
        refresh();
      };
      refresh();
      window.addEventListener(STORED_SELECTION_EVENT, refresh);
      window.addEventListener("storage", handleStorage);
      return () => {
        window.removeEventListener(STORED_SELECTION_EVENT, refresh);
        window.removeEventListener("storage", handleStorage);
      };
    }, [currentCorp]);

    const { data: hierarchy, isFailed: isHierarchyFailed } =
      useGetAdminGroupHierarchy({
        corpAccountNbr: String(currentCorp ?? ""),
        isLbaEnabled: lbaEnabled,
      });

    const isFlagResolving = !isLbaReady && Boolean(selection?.id);

    const scope = useMemo<WayfinderScope>(() => {
      if (!lbaEnabled) return NO_SCOPE;
      // Fail OPEN on hierarchy failure (deliberately unlike INVALID below):
      // the backend enforces each admin's visibility regardless of the scope
      // sent, so an unscoped request can only return data the admin may see.
      // The breadcrumb renders disabled here, so the UI never claims a
      // filter that isn't applied; the stored selection is untouched and
      // re-applies once the hierarchy loads. isFailed (not isError) keeps
      // the suppression through retries -- isError blinks false mid-retry,
      // briefly re-applying the scope on every navigation.
      if (isHierarchyFailed) return NO_SCOPE;
      if (!selection?.id || !selection.type) return NO_SCOPE;

      if (selection.type === "ADMIN_GROUP") {
        if (hierarchy?.id && hierarchy.id === selection.id) return NO_SCOPE;
        return { kind: "ADMIN_GROUP", id: String(selection.id) };
      }

      if (selection.type === "LOCATION") {
        // Location externalIds may be numeric or string -- normalize to
        // string. Only a blank id is unresolvable: fail closed rather than
        // dropping the scope (the breadcrumb would still show the location
        // name while data went unscoped).
        const locationId = String(selection.id).trim();
        if (!locationId) return INVALID_SCOPE;
        return { kind: "LOCATION", id: locationId };
      }

      return NO_SCOPE;
    }, [lbaEnabled, isHierarchyFailed, selection, hierarchy?.id]);

    return { scope, isLbaEnabled: lbaEnabled, isFlagResolving };
  };
